import { Col, Form, Row } from 'antd';
import { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { mask, phoneMask } from '../../../shared/utils/functions';
import {
  AInput,
  ASelect,
  ASwitch,
  ContainerForm,
  OptionFooter,
  PageIntro,
  PageLayout,
  PagePanel,
} from '../../../components';
import { IAccessGroup } from '../../../shared/interfaces';
import { useToast } from '../../../hooks/toast';
import { schemaCreateUser, schemaEditUser } from '../schemas/yup.user.schema';
import { UserOutlined } from '@ant-design/icons';
import {
  getSelectAccessGroups,
  getUser,
  patchUser,
  postUser,
} from '../../../http';
import { useI18n } from '../../../shared/i18n';

const UserCreateEdit: React.FC = () => {
  const { id } = useParams();
  const { addToast } = useToast();
  const navigate = useNavigate();
  const { t } = useI18n();
  const [name, setName] = useState<string>();
  const [cpf, setCpf] = useState<string>();
  const [birthday, setBirthday] = useState<string>();
  const [accessGroupId, setAccessGroupId] = useState<string>();
  const [phone, setPhone] = useState<string>();
  const [email, setEmail] = useState<string>();
  const [password, setPassword] = useState<string>();
  const [confirmPassword, setConfirmPassword] = useState<string | undefined>();
  const [status, setStatus] = useState<boolean>(false);
  const [isCrcAtendente, setIsCrcAtendente] = useState<boolean>(false);
  const [accessGroups, setAccessGroups] = useState<IAccessGroup[]>([]);

  const loadData = useCallback(async () => {
    const data = await getUser(id);
    setName(data.name);
    setCpf(data.cpf);
    setBirthday(data.birthday);
    setPhone(data.phone);
    setEmail(data.email);
    setStatus(data.status);
    setAccessGroupId(data.access_group_id);
    setIsCrcAtendente(Boolean((data as any).is_crc_atendente));
  }, []);

  const handleEditSubmit = useCallback(async () => {
    schemaEditUser
      .validate({
        name,
        cpf,
        birthday,
        phone,
        email,
        password,
        confirmPassword,
        access_group_id: accessGroupId,
        status,
      })
      .then(async (formValidated: any) => {
        await patchUser(id, {
          ...formValidated,
          is_crc_atendente: isCrcAtendente,
        })
          .then(() => {
            addToast({
              type: 'success',
              title: t('usersPage.toast.updatedTitle'),
              description: `Dados atualizados. Atendente CRC: ${
                isCrcAtendente ? 'SIM - habilitado' : 'NÃO - desabilitado'
              }`,
            });
          })
          .catch((error) => {
            addToast({
              type: 'error',
              title: t('usersPage.toast.errorTitle'),
              description:
                error.response.data.error ?? t('usersPage.toast.editError'),
            });
          });
      })
      .catch((err) => {
        addToast({
          type: 'error',
          title: t('usersPage.toast.blankFields'),
          description: err.errors,
        });
        return;
      });
  }, [
    addToast,
    id,
    birthday,
    cpf,
    email,
    name,
    phone,
    status,
    accessGroupId,
    t,
    isCrcAtendente,
  ]);

  const handleCreateSubmit = useCallback(async () => {
    schemaCreateUser
      .validate({
        name,
        cpf,
        birthday,
        phone,
        email,
        access_group_id: accessGroupId,
        password,
        confirmPassword,
      })
      .then(async (formValidated: any) => {
        await postUser({
          ...formValidated,
          status: true,
          is_crc_atendente: isCrcAtendente,
        })
          .then(() => {
            addToast({
              type: 'success',
              title: t('usersPage.toast.createdTitle'),
              description: `${t('usersPage.toast.createdDescription')} ${
                isCrcAtendente ? ' (Habilitado como Atendente CRC)' : ''
              }`,
            });
            setTimeout(function () {
              navigate(-1);
            }, 1100);
          })
          .catch((error) => {
            addToast({
              type: 'error',
              title: t('usersPage.toast.errorTitle'),
              description:
                error.response.data.error ?? t('usersPage.toast.createError'),
            });
          });
      })
      .catch((err) => {
        addToast({
          type: 'error',
          title: t('usersPage.toast.blankFields'),
          description: err.errors,
        });
        return;
      });
  }, [
    addToast,
    name,
    birthday,
    cpf,
    phone,
    email,
    password,
    confirmPassword,
    t,
    isCrcAtendente,
    accessGroupId,
  ]);

  const listAccessGroups = useCallback(async () => {
    try {
      const data = await getSelectAccessGroups('all');
      setAccessGroups(data);
    } catch {
      addToast({
        type: 'error',
        title: t('usersPage.toast.errorTitle'),
        description: t('usersPage.toast.listAccessGroupsError'),
      });
    }
  }, [addToast, t]);

  useEffect(() => {
    listAccessGroups();
    if (id !== 'new') {
      loadData();
    }
  }, [id]);

  return (
    <PageLayout>
      <PageIntro
        badge={t('usersPage.intro.badge')}
        icon={<UserOutlined />}
        title={
          id === 'new'
            ? t('usersPage.intro.createTitle')
            : t('usersPage.intro.editTitle')
        }
        description={t('usersPage.intro.formDescription')}
      />
      <PagePanel>
        <ContainerForm>
          <Form layout="vertical" style={{ width: '90%' }}>
            <Row>
              <Col span={24}>
                <Form.Item label={t('usersPage.fields.name')} required>
                  <AInput
                    placeholder={t('usersPage.fields.namePlaceholder')}
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                  />
                </Form.Item>
              </Col>
            </Row>
            <Row>
              <Col span={8}>
                <Form.Item label={t('usersPage.fields.cpf')} required>
                  <AInput
                    placeholder={t('usersPage.fields.cpfPlaceholder')}
                    value={cpf}
                    maxLength={14}
                    onChange={(e) => setCpf(mask(e.target.value))}
                  />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item label={t('usersPage.fields.birthDate')} required>
                  <AInput
                    value={birthday}
                    type="date"
                    onChange={(e) => setBirthday(e.target.value)}
                  />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item label={t('usersPage.fields.accessGroup')} required>
                  <ASelect
                    value={accessGroupId}
                    onChange={(e) => setAccessGroupId(e)}
                    allowClear
                    options={accessGroups.map((opt: IAccessGroup) => ({
                      label: opt.description,
                      value: opt.id,
                    }))}
                  />
                </Form.Item>
              </Col>
            </Row>
            <Row>
              <Col span={12}>
                <Form.Item label={t('usersPage.fields.phone')} required>
                  <AInput
                    placeholder={t('usersPage.fields.phonePlaceholder')}
                    value={phone}
                    maxLength={15}
                    onChange={(e) => setPhone(phoneMask(e.target.value))}
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item label={t('usersPage.fields.loginEmail')} required>
                  <AInput
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                </Form.Item>
              </Col>
            </Row>
            {id === 'new' && (
              <Row>
                <Col span={12}>
                  <Form.Item label={t('usersPage.fields.password')} required>
                    <AInput
                      placeholder={t('usersPage.fields.passwordPlaceholder')}
                      value={password}
                      type="password"
                      onChange={(e) => setPassword(e.target.value)}
                    />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    label={t('usersPage.fields.passwordConfirmation')}
                    required
                  >
                    <AInput
                      placeholder={t(
                        'usersPage.fields.passwordConfirmationPlaceholder',
                      )}
                      value={confirmPassword}
                      type="password"
                      onChange={(e) => setConfirmPassword(e.target.value)}
                    />
                  </Form.Item>
                </Col>
              </Row>
            )}
            <Row gutter={[16, 4]}>
              {id !== 'new' && (
                <Col xs={24} md={12}>
                  <Form.Item label={t('usersPage.fields.active')}>
                    <ASwitch
                      checked={status}
                      checkedChildren={t('common.yes')}
                      unCheckedChildren={t('common.no')}
                      onChange={() => {
                        setStatus(!status);
                      }}
                    />
                  </Form.Item>
                </Col>
              )}
              <Col xs={24} md={12}>
                <Form.Item
                  label="Atendente CRC"
                  tooltip="Quando SIM, o usuário aparece na lista 'Atendente Responsável' das telas do CRC e pode ser atribuído a contatos e pendências."
                >
                  <ASwitch
                    checked={isCrcAtendente}
                    checkedChildren="Sim (habilitado)"
                    unCheckedChildren="Não (desabilitado)"
                    onChange={(v: boolean) => setIsCrcAtendente(v)}
                  />
                  <div
                    style={{
                      marginTop: 4,
                      fontSize: 11,
                      color: isCrcAtendente ? '#1677ff' : '#8c8c8c',
                      lineHeight: 1.3,
                    }}
                  >
                    {isCrcAtendente
                      ? '✅ Aparece no Select "Atendente Responsável" do CRC Central de Contatos.'
                      : '❌ Não aparece como opção de responsável nas telas do Relacionamento (CRC).'}
                  </div>
                </Form.Item>
              </Col>
            </Row>
            <OptionFooter
              onClickCancel={() => navigate(-1)}
              onClickOk={id !== 'new' ? handleEditSubmit : handleCreateSubmit}
            />
          </Form>
        </ContainerForm>
      </PagePanel>
    </PageLayout>
  );
};

export default UserCreateEdit;
